import { Inbox } from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge } from '@/components/ui/status-badge';
import { JOB_REQUEST_TONES, useJobRequests } from '@/hooks/job-requests';
import { formatDay, formatDuration, formatInstant, formatWallTime } from '@/lib/dates';
import { ChangeRequestQueue } from '@/pages/admin/job-requests/ChangeRequestQueue';
import {
  ApproveRequestDialog,
  DeclineRequestDialog,
} from '@/pages/admin/job-requests/ReviewDialogs';
import {
  JOB_REQUEST_STATUS_LABELS,
  type AdminJobRequestView,
  type JobRequestStatusValue,
} from '@shared/schemas/job-request';
import { DEFAULT_PAGE_SIZE, RECORD_SORTS } from '@shared/schemas/pagination';
import { SortSelect } from '@/components/ui/sort-select';
import type { RecordSort } from '@shared/schemas/pagination';

/*
 * /admin/job-requests — ROADMAP.md §5.5.
 *
 * The prototype's Pending / Approved / Declined / All pills, kept — with counts
 * on them, so "Pending 3" is visible before anything is clicked.
 *
 * Cards rather than a table, which is the client portal's pattern ported inward
 * (FINDINGS.md §10.1): each row here carries a paragraph of client notes and two
 * actions, and that is the shape a table clips.
 */

const STATUS_FILTERS: { value: JobRequestStatusValue | 'all'; label: string }[] = [
  { value: 'pending', label: JOB_REQUEST_STATUS_LABELS.pending },
  { value: 'approved', label: JOB_REQUEST_STATUS_LABELS.approved },
  // **Declined.** Not "rejected", not "Not Approved" — the prototype uses three
  // words for one state (P11, P18), and this label comes from the same map as
  // the badge and the client's notification.
  { value: 'declined', label: JOB_REQUEST_STATUS_LABELS.declined },
  { value: 'all', label: 'All' },
];

export function JobRequestsPage() {
  const [status, setStatus] = useState<JobRequestStatusValue | 'all'>('pending');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<RecordSort>('newest');
  const [approving, setApproving] = useState<AdminJobRequestView | null>(null);
  const [declining, setDeclining] = useState<AdminJobRequestView | null>(null);

  const requests = useJobRequests({ sort, page, status });
  const counts = requests.data?.counts;

  return (
    <AdminPage
      title="Job Requests"
      description="What clients have asked for, waiting to be approved, scheduled or declined."
    >
      {/*
        Changes to booked cleans sit above new requests, because they are about
        work already promised — a client asking to move Thursday's clean is more
        urgent than one asking for a clean at some point. ROADMAP.md §5.6.
      */}
      <ChangeRequestQueue />

      <h2 className="mb-3 text-h2 text-ink-900">New requests</h2>

      <div className="mb-4">
        <FilterPills
          label="Filter requests by status"
          options={STATUS_FILTERS.map((option) => ({
            ...option,
            ...(counts === undefined ? {} : { count: counts[option.value] }),
          }))}
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
        />
      </div>

      {/* The order control. A select rather than clickable headers —

          the list becomes cards below 768px and a card has no headers.

          Above the loading/error/list conditional, so it does not

          vanish on the two screens where somebody might reach for it. */}

      <div className="mb-3 flex justify-end">
        <SortSelect
          label="Sort job requests"

          options={RECORD_SORTS}

          value={sort}

          onChange={(next) => {
            setSort(next);

            // Page 1, or you are on page 3 of a differently ordered list.

            setPage(1);
          }}
        />
      </div>

      {requests.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            onRetry={() => {
              void requests.refetch();
            }}
          />
        </div>
      ) : requests.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading requests</span>
          <div aria-hidden="true" className="h-48 animate-pulse rounded-lg bg-ink-100" />
        </div>
      ) : requests.data && requests.data.data.length > 0 ? (
        <ul className="space-y-3">
          {requests.data.data.map((entry) => (
            <RequestCard
              key={entry.id}
              request={entry}
              onApprove={() => {
                setApproving(entry);
              }}
              onDecline={() => {
                setDeclining(entry);
              }}
            />
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={Inbox}
            title={
              status === 'pending' ? 'Nothing waiting on you.' : 'No requests with that status.'
            }
            hint={
              status === 'pending'
                ? 'When a client asks for a clean through their portal, it lands here.'
                : 'Try another filter.'
            }
          />
        </div>
      )}

      {requests.data ? (
        <Pagination
          page={requests.data.page}
          pageSize={requests.data.pageSize || DEFAULT_PAGE_SIZE}
          total={requests.data.total}
          onPageChange={setPage}
          itemNoun="requests"
        />
      ) : null}

      <ApproveRequestDialog
        request={approving}
        onClose={() => {
          setApproving(null);
        }}
      />
      <DeclineRequestDialog
        request={declining}
        onClose={() => {
          setDeclining(null);
        }}
      />
    </AdminPage>
  );
}

function RequestCard({
  request,
  onApprove,
  onDecline,
}: {
  request: AdminJobRequestView;
  onApprove: () => void;
  onDecline: () => void;
}) {
  return (
    <li className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <span className="text-h3 text-ink-900">{request.clientName}</span>
            <StatusBadge label={request.statusLabel} tone={JOB_REQUEST_TONES[request.status]} />
          </p>
          <p className="mt-0.5 text-meta text-ink-500">
            Asked {formatInstant(request.submittedAt)}
            {request.clientPhone ? ` · ${request.clientPhone}` : ''}
          </p>
        </div>

        {request.status === 'pending' ? (
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button onClick={onApprove}>Approve</Button>
            <Button variant="outline" onClick={onDecline}>
              Decline
            </Button>
          </div>
        ) : null}
      </div>

      <dl className="mt-3 grid gap-x-6 gap-y-1.5 text-body text-ink-700 sm:grid-cols-2">
        <Row label="Address">
          {request.propertyAddress ?? (
            <span className="italic text-ink-500">That address has been removed</span>
          )}
        </Row>
        <Row label="Service">
          {/* `serviceTypeName`, the display value — never a slug (P5). */}
          {request.serviceTypeName ?? (
            <span className="italic text-ink-500">Not chosen — they want a suggestion</span>
          )}
        </Row>
        <Row label="Preferred">
          {request.preferredDate ? (
            <>
              {formatDay(request.preferredDate)}
              {request.preferredTime ? ` at ${formatWallTime(request.preferredTime)}` : ''}
            </>
          ) : (
            <span className="italic text-ink-500">No date given</span>
          )}
        </Row>
        <Row label="They estimated">
          {request.estimatedDurationMinutes === null ? (
            <span className="italic text-ink-500">Left blank</span>
          ) : (
            formatDuration(request.estimatedDurationMinutes)
          )}
        </Row>
      </dl>

      {request.notes ? (
        <p className="mt-3 rounded-sm border border-ink-300 bg-ink-100/40 px-3 py-2.5 text-body text-ink-900">
          <span className="text-meta text-ink-500">Their notes: </span>
          {request.notes}
        </p>
      ) : null}

      {request.reviewNote ? (
        <p className="mt-3 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-body text-bark-900">
          <span className="text-meta">
            Sent to the client{request.reviewedByName ? ` by ${request.reviewedByName}` : ''}:{' '}
          </span>
          {request.reviewNote}
        </p>
      ) : null}

      {request.createdJobNumber ? (
        <p className="mt-3 text-meta text-ink-500">
          Booked as{' '}
          <a
            href={`/admin/jobs/${request.createdJobId ?? ''}`}
            className="text-bark-700 underline underline-offset-2"
          >
            {request.createdJobNumber}
          </a>
        </p>
      ) : null}
    </li>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-ink-500">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}
